/**
 * Users Management — wired to /users API
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Tooltip,
  Drawer,
  Checkbox,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  MailOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import userService, { User } from '@/services/user.service';
import { useAuthStore } from '@/store/authStore';
import { promptHandoverIfNeeded } from '@/components/moc/handoverPrompt';

const { Title, Text } = Typography;
const { Search } = Input;

type RowUser = User & {
  roles_summary?: { code: string; name: string }[];
};

function isUserLocked(u: { locked_until?: string | null }): boolean {
  if (!u.locked_until) return false;
  return dayjs(u.locked_until).isAfter(dayjs());
}

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<RowUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [permDrawerUser, setPermDrawerUser] = useState<RowUser | null>(null);
  const [effectivePerms, setEffectivePerms] = useState<string[]>([]);
  const [permLoading, setPermLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState<RowUser | null>(null);
  const [searchText, setSearchText] = useState('');
  const [resetForm] = Form.useForm();

  const isSuperAdmin = !!authUser?.is_super_admin;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const list = await userService.getAll({
        search: searchText.trim() || undefined,
      });
      setUsers(list as RowUser[]);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [searchText]);

  useEffect(() => {
    const t = setTimeout(() => loadUsers(), 300);
    return () => clearTimeout(t);
  }, [loadUsers]);

  const handleDeleteUser = async (userId: string) => {
    try {
      setLoading(true);
      await userService.delete(userId);
      message.success('User deleted');
      loadUsers();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId: string, checked: boolean) => {
    try {
      if (checked) {
        await userService.activate(userId);
      } else {
        const res = await userService.deactivate(userId);
        const u = users.find((x) => x.id === userId);
        promptHandoverIfNeeded({
          summary: res?.responsibility_summary,
          userId,
          personLabel: u ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email : 'This user',
          reason: 'termination',
          navigate,
        });
      }
      message.success(checked ? 'Activated' : 'Deactivated');
      loadUsers();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Update failed');
    }
  };

  const handleUnlock = async (userId: string) => {
    try {
      setLoading(true);
      await userService.unlock(userId);
      message.success('Account unlocked');
      loadUsers();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Unlock failed');
    } finally {
      setLoading(false);
    }
  };

  const openResetPassword = (u: RowUser) => {
    setResetTarget(u);
    resetForm.resetFields();
    setResetModalVisible(true);
  };

  const submitResetPassword = async () => {
    if (!resetTarget) return;
    try {
      const v = await resetForm.validateFields();
      await userService.adminResetPassword(
        resetTarget.id,
        v.new_password,
        v.password_must_change
      );
      message.success('Password updated');
      setResetModalVisible(false);
      setResetTarget(null);
    } catch (e: any) {
      if (!e?.errorFields) {
        message.error(e?.response?.data?.message || 'Reset failed');
      }
    }
  };

  const openPermissions = async (u: RowUser) => {
    setPermDrawerUser(u);
    setPermLoading(true);
    setEffectivePerms([]);
    try {
      const codes = await userService.getEffectivePermissions(u.id);
      setEffectivePerms(codes);
    } catch {
      message.error('Could not load permissions');
    } finally {
      setPermLoading(false);
    }
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      width: 140,
      render: (text: string, record: RowUser) => (
        <Space wrap>
          <UserOutlined />
          <Text strong>{text}</Text>
          {!record.is_active && <Tag>Inactive</Tag>}
          {record.is_super_admin && <Tag color="red">Super</Tag>}
          {isUserLocked(record) && (
            <Tag color="orange">
              Locked until {dayjs(record.locked_until).format('DD MMM YYYY HH:mm')}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Name',
      key: 'name',
      width: 160,
      render: (_: unknown, r: RowUser) =>
        `${r.first_name || ''} ${r.last_name || ''}`.trim() || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      render: (text: string) => (
        <Space>
          <MailOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: 'Roles',
      key: 'roles',
      width: 220,
      render: (_: unknown, r: RowUser) => (
        <>
          {(r.roles_summary || []).map((x) => (
            <Tag key={x.code} color="blue" style={{ marginBottom: 4 }}>
              {x.name}
            </Tag>
          ))}
          {!r.roles_summary?.length && <Text type="secondary">—</Text>}
        </>
      ),
    },
    {
      title: 'Last login',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 160,
      render: (t: string) => t?.replace('T', ' ').slice(0, 19) || 'Never',
    },
    {
      title: 'Active',
      key: 'is_active',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, r: RowUser) => (
        <Switch
          checked={r.is_active}
          disabled={r.is_super_admin && !isSuperAdmin}
          onChange={(c) => handleToggleActive(r.id, c)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      fixed: 'right' as const,
      render: (_: unknown, r: RowUser) => (
        <Space>
          <Tooltip title="Effective permissions">
            <Button
              type="link"
              icon={<SafetyCertificateOutlined />}
              onClick={() => openPermissions(r)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              disabled={r.is_super_admin && !isSuperAdmin}
              onClick={() => navigate(`/platform/security/users/${r.id}/edit`)}
            />
          </Tooltip>
          <Tooltip title="Reset password">
            <Button
              type="link"
              icon={<KeyOutlined />}
              disabled={r.is_super_admin && !isSuperAdmin}
              onClick={() => openResetPassword(r)}
            />
          </Tooltip>
          {isUserLocked(r) && (
            <Popconfirm
              title="Unlock this account?"
              description="Clears failed login attempts and temporary lockout."
              onConfirm={() => handleUnlock(r.id)}
              disabled={r.is_super_admin && !isSuperAdmin}
            >
              <Tooltip title="Unlock account">
                <Button
                  type="link"
                  icon={<UnlockOutlined />}
                  disabled={r.is_super_admin && !isSuperAdmin}
                />
              </Tooltip>
            </Popconfirm>
          )}
          <Popconfirm
            title="Delete this user?"
            onConfirm={() => handleDeleteUser(r.id)}
            disabled={r.is_super_admin}
          >
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                disabled={r.is_super_admin}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <SmartBreadcrumbs />

      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <UserOutlined /> Users
            </Title>
            <Text type="secondary">
              Create users, assign roles, reset passwords (company-scoped)
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/platform/security/users/new')}
            >
              Create user
            </Button>
          </Col>
        </Row>

        <Row style={{ marginBottom: 16 }}>
          <Col span={10}>
            <Search
              placeholder="Search username, email, name"
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
        </Row>

        <Table<RowUser>
          columns={columns as any}
          dataSource={users}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t) => `${t} users` }}
        />
      </Card>

      <Modal
        title={`Reset password — ${resetTarget?.username || ''}`}
        open={resetModalVisible}
        onOk={submitResetPassword}
        onCancel={() => {
          setResetModalVisible(false);
          setResetTarget(null);
        }}
        destroyOnClose
      >
        <Form form={resetForm} layout="vertical">
          <Alert
            type="info"
            showIcon
            message="Min 10 chars, uppercase, lowercase, and digit"
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            name="new_password"
            label="New password"
            rules={[{ required: true, min: 10 }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="password_must_change"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>Require change on next login</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={`Permissions — ${permDrawerUser?.username}`}
        open={!!permDrawerUser}
        onClose={() => setPermDrawerUser(null)}
        width={480}
      >
        {permLoading ? (
          <Text>Loading…</Text>
        ) : (
          <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
            {effectivePerms.map((c) => (
              <Tag key={c} style={{ margin: 4 }}>
                {c}
              </Tag>
            ))}
            {!effectivePerms.length && <Text type="secondary">No permissions</Text>}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default UsersPage;
